import { Matrix3, Matrix4, Sphere, Vector3 } from "three";
import { Cartographic } from "./Cartographic";
/**
 * Mathematical class that represents the definition of an ellipsoid. Contains a collection of pre-computed values
 * that can be used by other math code for working with the defined ellipsoid
 */
export class Ellipsoid {
    /**
     * Constructs a new ellipsoid instance with the provided radii parameters
     * @param x The radius along the X-axis
     * @param y The radius along the Y-axis
     * @param z The radius along the Z-axis
     */
    constructor(x, y, z) {
        this._radii = new Vector3(x, y, z);
        this._radiiSquared = this._radii.clone().multiply(this._radii);
        this._oneOverRadii = new Vector3(1 / x, 1 / y, 1 / z);
        this._oneOverRadiiSquared = new Vector3(1 / (x * x), 1 / (y * y), 1 / (z * z));
        this._radiiToTheFourth = this._radiiSquared.clone().multiply(this._radiiSquared);
        this._minimumRadius = Math.min(x, y, z);
        this._maximumRadius = Math.max(x, y, z);
    }
    /**
     * Returns the geocentric normal, given a WGS 84 position on the surface of the ellipsoid.
     * @param position WGS 84 position on the surface of the ellipsoid
     * @param result Vector to write the result into
     */
    geocentricSurfaceNormalForPosition(position, result = new Vector3()) {
        return result.copy(result).normalize();
    }
    /**
     * Returns the geodetic normal, given a WGS 84 position on the surface of the ellipsoid.
     * @param position WGS 84 position on the surface of the ellipsoid
     * @param result Vector to write the result into
     */
    geodeticSurfaceNormalForPosition(position, result = new Vector3()) {
        return result.copy(position).multiply(this._oneOverRadiiSquared).normalize();
    }
    /**
     * Returns the geodetic surface normal for the provided cartographic position
     * @param position Cartographic position to sample the normal for
     * @param result Vector to write the result into
     */
    geodeticSurfaceNormalForCartographic(position, result = new Vector3()) {
        const cosLatitude = Math.cos(position.latitude);
        return result.set(cosLatitude * Math.cos(position.longitude), cosLatitude * Math.sin(position.longitude), Math.sin(position.latitude));
    }
    /**
     * Returns the cartographic position that corresponds to the provided position on the surface of the ellipsoid.
     *
     * Will not return useful results of 'position' does not lie on the surface of the ellipsoid.
     *
     * @param position Cartesian position on surface of ellipsoid to compute the cartographic position of
     * @param result Value to write the result into
     */
    cartographicForPositionOnSurface(position, result = new Cartographic()) {
        const geodeticNormal = this.geodeticSurfaceNormalForPosition(position);
        result.longitude = Math.atan2(geodeticNormal.y, geodeticNormal.x);
        result.latitude = Math.asin(geodeticNormal.z / geodeticNormal.length());
        return result;
    }
    /**
     * Returns the cartographic position and height for the provided cartesian position.
     *
     * Will behave poorly if the point is too close to the center of the ellipsoid. This uses an iterative root finding
     * process that may explode when the point is too close to the origin. It is the caller's responsibility to ensure
     * this doesn't happen.
     * @param position
     * @param result The object onto which to store the result.
     */
    cartographicForPosition(position, result = new Cartographic()) {
        const p = this.scaleToGeodeticSurface(position);
        const h = position.clone().sub(p);
        this.cartographicForPositionOnSurface(p, result);
        result.height = Math.sign(h.dot(position)) * h.length();
        return result;
    }
    /**
     * Produces a cartesian position around the ellipsoid for the provided cartographic position
     * @param cartographic
     * @param result The object onto which to store the result.
     */
    cartesianForCartographic(cartographic, result = new Vector3()) {
        const n = this.geodeticSurfaceNormalForCartographic(cartographic);
        const k = n.clone().multiply(this._radiiSquared);
        const gamma = Math.sqrt(n.dot(k));
        k.divideScalar(gamma);
        n.multiplyScalar(cartographic.height);
        return result.copy(k).add(n);
    }
    /**
     * Scales the provided Cartesian position along the geodetic surface normal so that it is on the surface of this
     * ellipsoid.
     *
     * Will behave poorly if the point is too close to the center of the ellipsoid. This uses an iterative root finding
     * process that may explode when the point is too close to the origin. It is the caller's responsibility to ensure
     * this doesn't happen.
     * @param position The position position to scale.
     * @param result The object onto which to store the result.
     *
     * @function scaleToGeodeticSurface
     */
    scaleToGeodeticSurface(position, result = new Vector3()) {
        const beta = 1.0 /
            Math.sqrt(position.x * position.x * this._oneOverRadiiSquared.x +
                position.y * position.y * this._oneOverRadiiSquared.y +
                position.z * position.z * this._oneOverRadiiSquared.z);
        const n = new Vector3(beta * position.x * this._oneOverRadiiSquared.x, beta * position.y * this._oneOverRadiiSquared.y, beta * position.z * this._oneOverRadiiSquared.z).length();
        let alpha = (1.0 - beta) * (position.length() / n);
        const x2 = position.x * position.x;
        const y2 = position.y * position.y;
        const z2 = position.z * position.z;
        let da = 0.0;
        let db = 0.0;
        let dc = 0.0;
        let s = 0.0;
        let dSdA = 1.0;
        do {
            alpha -= s / dSdA;
            da = 1.0 + alpha * this._oneOverRadiiSquared.x;
            db = 1.0 + alpha * this._oneOverRadiiSquared.y;
            dc = 1.0 + alpha * this._oneOverRadiiSquared.z;
            const da2 = da * da;
            const db2 = db * db;
            const dc2 = dc * dc;
            const da3 = da * da2;
            const db3 = db * db2;
            const dc3 = dc * dc2;
            s =
                x2 / (this._radiiSquared.x * da2) +
                    y2 / (this._radiiSquared.y * db2) +
                    z2 / (this._radiiSquared.z * dc2) -
                    1.0;
            dSdA =
                -2.0 *
                    (x2 / (this._radiiToTheFourth.x * da3) +
                        y2 / (this._radiiToTheFourth.y * db3) +
                        z2 / (this._radiiToTheFourth.z * dc3));
        } while (Math.abs(s) > 1e-10);
        return result.set(position.x / da, position.y / db, position.z / dc);
    }
    /**
     * Scales the cartesian position along the geocentric normal to produce a point on the surface of the ellipsoid
     * @param position The position to scale
     * @param result The value to write the result into
     */
    scaleToGeocentricSurface(position, result = new Vector3()) {
        const beta = 1.0 /
            Math.sqrt(position.x * position.x * this._oneOverRadiiSquared.x +
                position.y * position.y * this._oneOverRadiiSquared.y +
                position.z * position.z * this._oneOverRadiiSquared.z);
        return result.copy(position).multiplyScalar(beta);
    }
    /**
     * Constructs a rotational matrix from the basis vectors sampled from the surface of the ellipsoid at the provided
     * cartographic position.
     *
     * Constructs a TBN (tangent, bitangent, normal) matrix by sampling the normal and tangent analytically and
     * reconstructing the bi-tangent as the cross product of the tangent and normal to produce the full basis.
     *
     * The x axis is the tangent
     * The y axis is the normal
     * The z axis is the bitangent
     *
     * @param position
     * @param result
     * @param normal
     * @param tangent
     * @param bitangent
     */
    rotationBasisForCartographic(position, result = new Matrix3(), normal = new Vector3(), tangent = new Vector3(), bitangent = new Vector3()) {
        // Sample the surface normal at the basis position
        this.geodeticSurfaceNormalForCartographic(position, normal);
        // Sample another normal at a slightly different position along the latitude plane. Cross product of normal
        // dn will give us our bitangent vector that is perpendicular to both normal and dn.
        const dn = this.geodeticSurfaceNormalForCartographic(position.clone().setLatitude(position.latitude - 0.01));
        bitangent.copy(normal).cross(dn).normalize();
        // Our tangent vector can now be found by the cross product of normal and our bitangent.
        tangent.copy(normal).cross(bitangent).normalize();
        // Construct a rotation matrix from the basis frame
        if (result !== null) {
            result.elements[0] = tangent.x;
            result.elements[1] = tangent.y;
            result.elements[2] = tangent.z;
            result.elements[3] = normal.x;
            result.elements[4] = normal.y;
            result.elements[5] = normal.z;
            result.elements[6] = bitangent.x;
            result.elements[7] = bitangent.y;
            result.elements[8] = bitangent.z;
        }
        return result;
    }
    /**
     * Checks if the given tile will be visible based on the normalized direction vector from the tile to the camera
     * that is observing the planet.
     *
     * This works by calculating the analytical surface normals of the ellipsoid at the corners of the tile and compares
     * them with the globeToCam dir to find if any of the ellipsoid patch is not back facing.
     *
     * If all the normals point away from the camera then the surface patch must be back-facing and can be culled
     * without meshing. If at least one normal points towards the camera then some non zero part of the surface may be
     * visible and so the tile must not be culled.
     * @param tileToCam The direction from the tile to the camera
     * @param min
     * @param max
     */
    regionVisibleFromViewDir(tileToCam, min, max) {
        const p = new Cartographic();
        const a = this.geodeticSurfaceNormalForCartographic(p.copy(min));
        const b = this.geodeticSurfaceNormalForCartographic(p.copy(max));
        const c = this.geodeticSurfaceNormalForCartographic(p.set(min.longitude, max.latitude));
        const d = this.geodeticSurfaceNormalForCartographic(p.set(max.longitude, min.latitude));
        const aPass = tileToCam.dot(a) > -0.1;
        const bPass = tileToCam.dot(b) > -0.1;
        const cPass = tileToCam.dot(c) > -0.1;
        const dPass = tileToCam.dot(d) > -0.1;
        return aPass || bPass || cPass || dPass;
    }
    /**
     * Calculates an approximate bounding sphere for the given spheroid patch.
     *
     * The resulting bounding sphere is not guaranteed to contain the entire surface of the ellipsoid. This is only
     * because I have not yet proven that it contains the whole surface patch. Until I have this will be marked as
     * approximate.
     * @param min The minimum long/lat pair of the patch to get the bounding sphere of
     * @param max The maximum long/lat pair of the patch to get the bounding sphere of
     */
    approximateBoundingSphereForRegion(min, max) {
        const avgLongitude = (min.longitude + max.longitude) / 2;
        const avgLatitude = (min.latitude + max.latitude) / 2;
        const c = new Cartographic();
        const points = [
            this.cartesianForCartographic(c.set(min.longitude, min.latitude)),
            this.cartesianForCartographic(c.set(max.longitude, max.latitude)),
            this.cartesianForCartographic(c.set(max.longitude, min.latitude)),
            this.cartesianForCartographic(c.set(min.longitude, max.latitude)),
            this.cartesianForCartographic(c.set(avgLongitude, avgLatitude)),
            this.cartesianForCartographic(c.set(min.longitude, avgLatitude)),
            this.cartesianForCartographic(c.set(max.longitude, avgLatitude)),
            this.cartesianForCartographic(c.set(avgLongitude, min.latitude)),
            this.cartesianForCartographic(c.set(avgLongitude, max.latitude)),
        ];
        const mid = new Vector3();
        for (const p of points) {
            mid.add(p);
        }
        mid.divideScalar(points.length);
        const scratch = new Vector3();
        let radius = 0;
        for (const p of points) {
            const length = scratch.copy(mid).sub(p).length();
            radius = Math.max(radius, length);
        }
        return new Sphere(mid, radius);
    }
    /**
     * Will update the given object's position so it will be placed at the specified cartographic position, rotated so
     * that the object's local up direction matches the local surface normal of the ellipsoid.
     *
     * This function allows placing objects on the surface of the ellipsoid so that up is defined relative to the
     * surface for that object.
     * @param object The object to place on the ellipsoid
     * @param cartographic The position to place the object
     */
    placeObjectFromCartographic(object, cartographic) {
        // Set the objects position based on the provided cartographic position
        this.cartesianForCartographic(cartographic, object.position);
        // Sample the rotation basis at that point on the surface and rotate the object into that rotation frame
        const basis = this.rotationBasisForCartographic(cartographic);
        object.setRotationFromMatrix(new Matrix4().setFromMatrix3(basis));
    }
    /**
     * Will update the given object's position so it will be placed at the specified ECEF cartesian position, rotated so
     * that the object's local up direction matches the local surface normal of the ellipsoid.
     *
     * This function allows placing objects on the surface of the ellipsoid so that up is defined relative to the
     * surface for that object.
     * @param object The object to place on the ellipsoid
     * @param position The position to place the object
     */
    placeObjectFromCartesian(object, position) {
        // Set the exact position
        object.position.copy(position);
        // Extract the cartographic position so we can sample the surface rotation basis
        const cartographic = this.cartographicForPosition(position);
        // Sample the rotation basis at the point on the surface above/below the specified ECEF position and rotate the
        // object into that rotation frame
        const basis = this.rotationBasisForCartographic(cartographic);
        object.setRotationFromMatrix(new Matrix4().setFromMatrix3(basis));
    }
    /**
     * Returns a new Vector3 instance that contains the radii of the ellipsoid along each axis
     */
    get radii() {
        return this._radii.clone();
    }
    /**
     * Returns a new Vector3 instance that contains the radii^2 of the ellipsoid along each axis
     */
    get radiiSquared() {
        return this._radiiSquared.clone();
    }
    /**
     * Returns a new Vector3 instance that contains the 1/radii of the ellipsoid along each axis
     */
    get oneOverRadii() {
        return this._oneOverRadii.clone();
    }
    /**
     * Returns a new Vector3 instance that contains the 1/radii^2 of the ellipsoid along each axis
     */
    get oneOverRadiiSquared() {
        return this._oneOverRadiiSquared.clone();
    }
    /**
     * Returns a new Vector3 instance that contains the radii^4 of the ellipsoid along each axis
     */
    get radiiToTheFourth() {
        return this._radiiToTheFourth.clone();
    }
    /**
     * Returns the minimum of the ellipsoid
     */
    get minimumRadius() {
        return this._minimumRadius;
    }
    /**
     * Returns the maximum of the ellipsoid
     */
    get maximumRadius() {
        return this._maximumRadius;
    }
}
/**
 * A pre-constructed Ellipsoid that defines the WGS84 ellipsoid
 */
Ellipsoid.WGS_84 = new Ellipsoid(6378137, 6378137, 6356752.314245);
/**
 * A pre-computed Ellipsoid that defines the WGS84 ellipsoid, scaled so that the X and Y radii = 1, and that the
 * Z radii is equal to the ratio of the semi-minor axis to the semi-major axis (b / a)
 */
Ellipsoid.SCALED_WGS_84 = new Ellipsoid(1, 1, 6356752.314245 / 6378137);
